import { ListMusic } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router'

import { usePlayer } from '@/audio/store'
import { Cover } from '@/components/Cover'
import { IconButton } from '@/components/ui'
import { cn } from '@/lib/cn'

import { FullScreenPlayer } from './FullScreenPlayer'
import { QueuePanel } from './QueuePanel'
import { SeekBar } from './SeekBar'
import {
  NextButton,
  PlayPauseButton,
  PreviousButton,
  RepeatButton,
  ShuffleButton,
} from './TransportControls'
import { VolumeControl } from './VolumeControl'

/**
 * The docked player.
 *
 * Mounted once by `AppShell` and never unmounted, so it must be exactly
 * `h-player` tall — the layout reserves that height for it. Positioning,
 * background, border and safe-area padding all come from the shell.
 */
export function PlayerBar() {
  const queue = usePlayer((state) => state.queue)
  const index = usePlayer((state) => state.index)
  const isBuffering = usePlayer((state) => state.isBuffering)
  const waiting = usePlayer((state) => state.awaitingContinuation)
  const error = usePlayer((state) => state.error)
  const currentTime = usePlayer((state) => state.currentTime)
  const duration = usePlayer((state) => state.duration)

  const [queueOpen, setQueueOpen] = useState(false)
  const [expandedOpen, setExpandedOpen] = useState(false)
  const expandedTrigger = useRef<HTMLButtonElement>(null)

  const location = useLocation()
  useEffect(() => {
    setQueueOpen(false)
    setExpandedOpen(false)
  }, [location.key])

  const current = index >= 0 && index < queue.length ? queue[index] : null
  if (!current) return null

  const progress = duration > 0 ? Math.min(100, (currentTime / duration) * 100) : 0
  const status = error ?? (waiting ? 'Getting a similar song ready…' : isBuffering ? 'Buffering…' : '')
  const subtitle = status || current.artistName

  return (
    <>
      <div className="relative h-player">
        {/* There is no room for a seek bar at phone widths, so the elapsed
            fraction is drawn as a hairline along the top edge instead. The
            shell's own top border already reads as the unfilled remainder, so
            this track stays transparent rather than doubling that line. */}
        <div aria-hidden="true" className="absolute inset-x-0 top-0 h-0.5 md:hidden">
          <div
            className="h-full bg-accent transition-[width] duration-200 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Announced separately so a track change does not read out the artist
            name every time, while errors and buffering still reach a reader. */}
        <p className="sr-only" role="status">
          {status}
        </p>

        <div className="flex h-full items-center gap-2 px-2 md:gap-4 md:px-4">
          <button
            ref={expandedTrigger}
            type="button"
            onClick={() => setExpandedOpen(true)}
            aria-label={`Now playing: ${current.title} by ${current.artistName}. Open the full screen player.`}
            className="flex h-14 min-w-0 flex-1 items-center gap-3 rounded-lg px-1 text-left transition-colors hover:bg-surface-hover md:max-w-64"
          >
            <Cover src={current.coverUrl} size="md" />
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate text-sm font-medium text-text">{current.title}</span>
              <span
                className={cn('truncate text-xs', error ? 'text-danger' : 'text-text-muted')}
              >
                {subtitle}
              </span>
            </span>
          </button>

          {/* Desktop: transport over a seek bar, centred in the bar. The min
              width keeps the five controls on one line when the left and right
              columns are competing for space at exactly the md breakpoint. */}
          <div className="hidden min-w-52 flex-1 flex-col items-center justify-center gap-1 md:flex">
            <div className="flex items-center gap-1">
              <ShuffleButton />
              <PreviousButton size="sm" />
              <PlayPauseButton size="sm" />
              <NextButton size="sm" />
              <RepeatButton />
            </div>
            <SeekBar className="max-w-xl" />
          </div>

          {/* Phone: just the two controls that matter, at full touch size. */}
          <div className="flex shrink-0 items-center gap-1 md:hidden">
            <PlayPauseButton size="md" />
            <NextButton size="md" />
          </div>

          {/* The queue button is NOT inside the desktop-only cluster below.
              It used to be, which meant a phone had no way to open the queue at
              all — the only way to see what was coming was the full screen
              player, and nothing there could be removed or reordered. */}
          <IconButton
            aria-label={`Queue, ${queue.length === 1 ? '1 song' : `${queue.length} songs`}`}
            size="sm"
            className="shrink-0 md:hidden"
            onClick={() => setQueueOpen(true)}
          >
            <ListMusic className="h-5 w-5" aria-hidden="true" />
          </IconButton>

          <div className="hidden min-w-0 flex-1 items-center justify-end gap-1 md:flex md:max-w-64">
            <VolumeControl />
            <IconButton
              aria-label={`Queue, ${queue.length === 1 ? '1 song' : `${queue.length} songs`}`}
              size="sm"
              onClick={() => setQueueOpen(true)}
            >
              <ListMusic className="h-5 w-5" aria-hidden="true" />
            </IconButton>
          </div>
        </div>
      </div>

      <QueuePanel open={queueOpen} onOpenChange={setQueueOpen} />
      <FullScreenPlayer open={expandedOpen} onOpenChange={setExpandedOpen} returnFocus={() => expandedTrigger.current?.focus({ preventScroll: true })} />
    </>
  )
}
